import type { EnumScreenLockingStatus } from '../../domain/models/deviceInfo.model';
import type { LogicalDeviceConnectivityInfoParams } from '../logical-device-connectivity-info/logicalDeviceConnectivityInfo.model';
import { LogicalDeviceConnectivityIconColor } from '../logical-device-connectivity-info/logicalDeviceConnectivityInfo.model';
import type { StatusChipsProps } from '../status-chips';
import type {
  HideInfo,
  LogicalDeviceSummaryCardConnectivity,
  LogicalDeviceSummaryCardProps,
  LogicalDeviceSummaryGeneral,
} from './logicalDeviceSummaryCard.model';
import { LogicalDeviceConnectivityStatus, ScreenLockingStatus } from './logicalDeviceSummaryCard.model';

export function getSignalStrength(
  quality: 'EXCELLENT' | 'GOOD' | 'POOR' | undefined,
): 'signal-celular-low' | 'signal-celular-mid' | 'signal-celular' {
  switch (quality) {
    case 'GOOD': {
      return 'signal-celular-mid';
    }
    case 'POOR': {
      return 'signal-celular-low';
    }
    default: {
      return 'signal-celular';
    }
  }
}

export function getSignalColor(quality: 'EXCELLENT' | 'GOOD' | 'POOR' | undefined): LogicalDeviceConnectivityIconColor {
  switch (quality) {
    case 'GOOD': {
      return LogicalDeviceConnectivityIconColor.Mid;
    }
    case 'POOR': {
      return LogicalDeviceConnectivityIconColor.Low;
    }
    case 'EXCELLENT': {
      return LogicalDeviceConnectivityIconColor.Succes;
    }
    default: {
      return LogicalDeviceConnectivityIconColor.Inherit;
    }
  }
}

export function getMobileNetworkIconColor(
  mobileNetwork: LogicalDeviceSummaryCardConnectivity['mobileNetwork'],
): LogicalDeviceConnectivityIconColor | undefined {
  return mobileNetwork?.status === 'active' ? getSignalColor(mobileNetwork.strength) : undefined;
}

function checkShouldDisplayConnectivityInfo(
  key: keyof LogicalDeviceSummaryCardConnectivity,
  hiddenConnectivity?: (keyof LogicalDeviceSummaryCardConnectivity)[],
): boolean {
  return hiddenConnectivity ? !hiddenConnectivity?.includes(key) : true;
}

export function getConnectivityInfoForGroup({
  connectivity,
  key,
  icon,
  overrideColor,
  hiddenConnectivity,
}: {
  readonly connectivity: LogicalDeviceSummaryCardProps['connectivity'];
  readonly key: keyof LogicalDeviceSummaryCardConnectivity;
  readonly icon: LogicalDeviceConnectivityInfoParams['icon'];
  readonly overrideColor?: LogicalDeviceConnectivityIconColor;
  readonly hiddenConnectivity?: (keyof LogicalDeviceSummaryCardConnectivity)[];
}): LogicalDeviceConnectivityInfoParams[] {
  return checkShouldDisplayConnectivityInfo(key, hiddenConnectivity)
    ? [
        {
          key,
          icon,
          label: connectivity?.[key]?.name ?? 'Unknown', //@Pedro: how will we translate this one?
          subtitle: connectivity?.[key]?.type,
          iconColor:
            overrideColor ??
            (connectivity?.[key]?.status === 'active'
              ? LogicalDeviceConnectivityIconColor.Succes
              : LogicalDeviceConnectivityIconColor.Inherit),
          tooltip: connectivity?.[key]?.tooltip,
        },
      ]
    : [];
}

export function getCurrentStatusLabel(logicalDeviceConnectivityCurrentStatus: LogicalDeviceConnectivityStatus): string {
  return logicalDeviceConnectivityCurrentStatus === LogicalDeviceConnectivityStatus.Online
    ? 'logicalDevice:currentConnectivityStatus.connected'
    : 'logicalDevice:currentConnectivityStatus.disconnected';
}

export function getConnectivityStatusLabel(isConnected?: boolean): string {
  return isConnected ? 'logicalDevice:connectivityStatus.online' : 'logicalDevice:connectivityStatus.offline';
}

export function getConnectivityStatusType(isConnected?: boolean): StatusChipsProps['status'] {
  return isConnected ? 'success' : 'inherit';
}

export function checkShouldShowConnectivityStatus(info: LogicalDeviceSummaryGeneral): boolean {
  return info.isConnected !== undefined || !!info.connectivityStatus;
}

export function checkShouldDisplayInfo(infoKey: HideInfo, hideInfoParam?: HideInfo[]): boolean {
  return hideInfoParam ? !hideInfoParam?.includes(infoKey) : true;
}

export function getScreenStatusLabel(screenLockingStatus?: EnumScreenLockingStatus): string {
  switch (screenLockingStatus) {
    case ScreenLockingStatus.LOCKING:
      return 'logicalDevice:logicalDevice.statusLabel.screenLockRequested';
    case ScreenLockingStatus.LOCKED:
      return 'logicalDevice:logicalDevice.statusLabel.isScreenLocked';
    case ScreenLockingStatus.UNLOCKING:
      return 'logicalDevice:logicalDevice.statusLabel.screenUnlockRequested';
    default:
      return '';
  }
}

export function getScreenStatusType(screenLockingStatus?: EnumScreenLockingStatus): StatusChipsProps['status'] {
  switch (screenLockingStatus) {
    case ScreenLockingStatus.LOCKING:
      return 'warning';
    case ScreenLockingStatus.UNLOCKING:
      return 'info';
    case ScreenLockingStatus.LOCKED:
      return 'error';
    default:
      return 'inherit';
  }
}

export function shouldDisplayScreenLockingStatus(screenLockingStatus?: EnumScreenLockingStatus): boolean {
  return !!screenLockingStatus && screenLockingStatus !== ScreenLockingStatus.UNLOCKED;
}
